import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Show, SignIn, SignUp, useAuth, SignOutButton } from "@clerk/react";
import { Toaster } from "./components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { api, setTokenGetter } from "./lib/api";
import Layout from "./components/Layout";
import SetupPage from "./pages/SetupPage";
import AdminDashboard from "./pages/AdminDashboard";
import UploadsPage from "./pages/UploadsPage";
import SearchPage from "./pages/SearchPage";
import TeamsPage from "./pages/TeamsPage";
import UsersPage from "./pages/UsersPage";
import RequestsPage from "./pages/RequestsPage";
import FundedPage from "./pages/FundedPage";
import EntrancePage from "./pages/EntrancePage";
import ReviewPage from "./pages/ReviewPage";
import SettingsPage from "./pages/SettingsPage";
import AuditPage from "./pages/AuditPage";
import LeaderDashboard from "./pages/LeaderDashboard";
import RepDashboard from "./pages/RepDashboard";
import MyEntrancePage from "./pages/MyEntrancePage";
import RepBatchesPage from "./pages/RepBatchesPage";

const FLOW = [["01", "Upload", "Millions of leads in, cleaned and de-duplicated."], ["02", "Request", "Leaders and reps pull their daily allowance."],
  ["03", "Push", "Straight into each rep's own Entrance campaign."], ["04", "Rotate", "Anything not texted or funded comes back around."]];

const AuthPage = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-white">
    <div className="hidden lg:flex relative overflow-hidden canvas border-r border-zinc-200 p-12 xl:p-16 flex-col justify-between" data-testid="auth-brand-panel">
      <svg className="absolute -right-10 -bottom-28 w-[500px] pointer-events-none" viewBox="0 0 600 260" fill="none" aria-hidden>
        <path d="M40 40 Q300 300 560 40" stroke="#58a80a" strokeWidth="58" strokeLinecap="butt" opacity="0.95" />
      </svg>
      <img src="/logo-full.png" alt="Choicelead.ai" className="h-10 w-auto self-start object-contain rise" />
      <div className="relative space-y-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 rise"><span className="live-dot" /><span className="eyebrow">Lead distribution, live</span></div>
        <h1 className="text-5xl xl:text-6xl font-black leading-[1.02] text-zinc-950 rise" style={{ animationDelay: "80ms" }}>
          Request. Approve.<br />Distribute. <span className="brand-text">Rotate.</span></h1>
        <p className="text-zinc-600 text-base max-w-md rise" style={{ animationDelay: "160ms" }}>Fresh leads to every rep, every day, pushed right into Entrance. Unused leads go back into rotation on their own.</p>
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          {FLOW.map(([n, t, d], i) => (
            <div key={n} className="panel p-4 rise" style={{ animationDelay: `${240 + i * 80}ms` }}>
              <div className="mono text-[11px] brand-text font-semibold">{n}</div>
              <div className="font-display font-bold text-zinc-950 mt-1">{t}</div>
              <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{d}</div>
            </div>))}
        </div>
      </div>
      <div className="relative text-xs text-zinc-500">Access is limited to approved staff accounts.</div>
    </div>
    <div className="flex flex-col items-center justify-center gap-8 p-6">
      <img src="/logo-full.png" alt="Choicelead.ai" className="lg:hidden h-9 w-auto object-contain" />
      {children}
    </div>
  </div>
);

function Blocked({ detail }) {
  const [owner, setOwner] = useState(true);
  useEffect(() => { api.get("/setup/status").then((r) => setOwner(r.data.owner_exists)); }, []);
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="panel p-8 max-w-md space-y-4" data-testid="access-blocked">
        <h1 className="text-2xl font-black">{detail?.code === "not_approved" || detail?.code === "disabled" ? "Access not approved" : "Can't open your account"}</h1>
        <p className="text-sm text-zinc-600">{detail?.message || "Your account is not enabled."}</p>
        {detail?.emails?.length > 0 && <p className="text-xs mono text-zinc-500">Signed in as {detail.emails.join(", ")}</p>}
        {!owner && <Link to="/setup" className="text-[#3f7d05] text-sm underline" data-testid="go-setup-link">Owner initial setup</Link>}
        <div><SignOutButton><button className="text-sm underline" data-testid="sign-out-btn">Sign out</button></SignOutButton></div>
      </div>
    </div>
  );
}

function Shell() {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [blocked, setBlocked] = useState(null);
  setTokenGetter(getToken);
  const load = async () => {
    try { setMe((await api.get("/me")).data); setBlocked(null); } catch (e) {
      const d = e?.response?.data?.detail;
      setBlocked(typeof d === "object" && d ? d : { message: `Could not load your account (${e?.response?.status || "network error"}${typeof d === "string" ? `: ${d}` : ""})` });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (blocked?.code === "owner_setup") return <SetupPage onDone={load} />;
  if (blocked) return <Routes><Route path="/setup" element={<SetupPage onDone={load} />} /><Route path="*" element={<Blocked detail={blocked} />} /></Routes>;
  if (!me) return <div className="min-h-screen canvas flex items-center justify-center gap-3 text-sm text-zinc-500" data-testid="app-loading"><img src="/c-mark.png" alt="" className="w-8 h-8 animate-pulse" />Loading…</div>;
  const pages = {
    admin: [["/", <AdminDashboard />], ["/uploads", <UploadsPage />], ["/leads", <SearchPage me={me} />], ["/teams", <TeamsPage />],
      ["/users", <UsersPage />], ["/requests", <RequestsPage />], ["/batches", <RepBatchesPage />], ["/funded", <FundedPage me={me} />], ["/entrance", <EntrancePage />], ["/my-entrance", <MyEntrancePage me={me} />],
      ["/review", <ReviewPage />], ["/settings", <SettingsPage />], ["/audit", <AuditPage />]],
    leader: [["/", <LeaderDashboard me={me} />], ["/mine", <RepDashboard me={me} />], ["/my-entrance", <MyEntrancePage />], ["/leads", <SearchPage me={me} />], ["/funded", <FundedPage me={me} />]],
    rep: [["/", <RepDashboard me={me} />], ["/my-entrance", <MyEntrancePage />], ["/leads", <SearchPage me={me} />], ["/funded", <FundedPage me={me} />]],
  }[me.role];
  return (
    <Layout me={me}>
      <Routes>{pages.map(([p, el]) => <Route key={p} path={p} element={el} />)}<Route path="*" element={<Navigate to="/" replace />} /></Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/*" element={<><Show when="signed-in"><Shell /></Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>} />
        </Routes>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
